import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { paymentCalculator } from '../../services/PaymentCalculator';
import {
    DollarSign,
    Clock,
    CheckCircle,
    Calendar,
    TrendingUp,
    AlertCircle,
    RefreshCw
} from 'lucide-react';

export default function ContractorPaymentsView() {
    const { currentUser } = useAuth();
    const [loading, setLoading] = useState(true);
    const [payments, setPayments] = useState([]);
    const [stats, setStats] = useState({
        totalEarnings: 0,
        totalPaid: 0,
        totalPending: 0,
        paymentCount: 0
    });

    useEffect(() => {
        if (currentUser) {
            loadPayments();
        }
    }, [currentUser]);

    const loadPayments = async () => {
        setLoading(true);

        try {
            // Get payments and stats
            const paymentsData = await paymentCalculator.getContractorPayments(currentUser.uid);
            const statsData = await paymentCalculator.getPaymentStats(currentUser.uid);

            setPayments(paymentsData);
            setStats(statsData);

        } catch (error) {
            console.error('Error loading payments:', error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            'pending': 'bg-yellow-100 text-yellow-800',
            'approved': 'bg-blue-100 text-blue-800',
            'paid': 'bg-green-100 text-green-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    const getStatusIcon = (status) => {
        if (status === 'pending') return <Clock className="w-4 h-4" />;
        if (status === 'approved') return <CheckCircle className="w-4 h-4" />;
        if (status === 'paid') return <DollarSign className="w-4 h-4" />;
        return null;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading your payments...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8 flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Payments</h1>
                        <p className="text-gray-600 mt-2">
                            Track your earnings and payment history
                        </p>
                    </div>
                    <button
                        onClick={loadPayments}
                        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <RefreshCw className="w-4 h-4" />
                        <span>Refresh</span>
                    </button>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    {/* Total Earnings */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Total Earnings</p>
                                <p className="text-3xl font-bold text-gray-900 mt-2">
                                    ${stats.totalEarnings.toFixed(2)}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                                <TrendingUp className="w-6 h-6 text-green-600" />
                            </div>
                        </div>
                    </motion.div>

                    {/* Total Paid */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Paid Out</p>
                                <p className="text-3xl font-bold text-gray-900 mt-2">
                                    ${stats.totalPaid.toFixed(2)}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                                <CheckCircle className="w-6 h-6 text-blue-600" />
                            </div>
                        </div>
                    </motion.div>

                    {/* Pending */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Pending</p>
                                <p className="text-3xl font-bold text-gray-900 mt-2">
                                    ${stats.totalPending.toFixed(2)}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                                <Clock className="w-6 h-6 text-yellow-600" />
                            </div>
                        </div>
                    </motion.div>

                    {/* Payment Count */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Total Payments</p>
                                <p className="text-3xl font-bold text-gray-900 mt-2">
                                    {stats.paymentCount}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                                <DollarSign className="w-6 h-6 text-purple-600" />
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Payments List */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="bg-white rounded-xl shadow-sm border border-gray-200"
                >
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-xl font-bold text-gray-900">Payment History</h2>
                    </div>

                    {payments.length > 0 ? (
                        <div className="divide-y divide-gray-200">
                            {payments.map((payment) => (
                                <div
                                    key={payment.id}
                                    className="p-6 hover:bg-gray-50 transition-colors"
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            {/* Job Info */}
                                            <div className="flex items-center space-x-3 mb-2">
                                                <h3 className="font-semibold text-gray-900">
                                                    {payment.jobType || 'Payment'}
                                                </h3>
                                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}>
                                                    {getStatusIcon(payment.status)}
                                                    <span className="ml-1">{payment.status}</span>
                                                </span>
                                            </div>

                                            {/* Details */}
                                            <div className="flex items-center space-x-4 text-sm text-gray-600">
                                                {payment.jobLocation && (
                                                    <span>{payment.jobLocation}</span>
                                                )}
                                                <span>
                                                    <Calendar className="w-4 h-4 inline mr-1" />
                                                    {new Date(payment.createdAt).toLocaleDateString()}
                                                </span>
                                                {payment.hoursWorked && (
                                                    <span>
                                                        {payment.hoursWorked} hrs × ${payment.hourlyRate}/hr
                                                    </span>
                                                )}
                                            </div>

                                            {/* Payment Notes */}
                                            {payment.approvalNote && (
                                                <div className="mt-2 p-2 bg-blue-50 rounded text-sm text-blue-800">
                                                    <strong>Note:</strong> {payment.approvalNote}
                                                </div>
                                            )}

                                            {payment.paidAt && (
                                                <div className="mt-2 text-sm text-green-600">
                                                    ✓ Paid on {new Date(payment.paidAt).toLocaleDateString()}
                                                </div>
                                            )}
                                        </div>

                                        {/* Amount */}
                                        <div className="ml-4 text-right">
                                            <p className="text-2xl font-bold text-gray-900">
                                                ${payment.calculatedAmount?.toFixed(2) || '0.00'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="p-12 text-center">
                            <DollarSign className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                No Payments Yet
                            </h3>
                            <p className="text-gray-600">
                                Your payment history will appear here once jobs are completed.
                            </p>
                        </div>
                    )}
                </motion.div>
            </div>
        </div>
    );
}
